import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: `linear-gradient(135deg, ${theme.colors?.blue5 || '#1C35BC'} 0%, ${
      theme.colors?.lightBlue || '#5B65DC'
    } 100%)`,
    zIndex: 9999,
  },
  containerVisible: {
    animation: '$fadeInLoader 400ms ease-out forwards',
  },
  containerHiding: {
    animation: '$fadeOutLoader 300ms ease-out forwards',
    pointerEvents: 'none',
  },
  fadeOut: {
    opacity: 0,
    pointerEvents: 'none',
  },
  brandSection: {
    textAlign: 'center',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'opacity 200ms ease-out',
  },
  contentHiding: {
    opacity: 0,
  },
  logo: {
    width: 90,
    height: 120,
    marginBottom: 12,
    willChange: 'opacity',
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
  },
  part1: {
    fill: 'white',
    opacity: 0.7,
    animation: '$fadeIn 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    animationDelay: '0s',
    willChange: 'opacity',
  },
  part2: {
    fill: 'white',
    opacity: 0.7,
    animation: '$fadeIn 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    animationDelay: '0.4s',
    willChange: 'opacity',
  },
  mainTitle: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 8,
    letterSpacing: '0.5px',
    color: 'white',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    transform: 'scaleX(1.15)',
  },
  subtitle: {
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 40,
    color: 'white',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  spinner: {
    width: 24,
    height: 24,
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: '$spin 1s linear infinite',
  },
  '@keyframes fadeInLoader': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  '@keyframes fadeOutLoader': {
    '0%': { opacity: 1 },
    '100%': { opacity: 0 },
  },
  '@keyframes fadeIn': {
    '0%': { opacity: 0.3 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.3 },
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

const PageLoader = ({ isHiding = false }) => {
  const classes = useStyles();

  const containerClasses = [
    classes.container,
    isHiding ? classes.containerHiding : classes.containerVisible,
  ].join(' ');

  const contentClasses = [classes.brandSection, isHiding ? classes.contentHiding : ''].join(' ');

  return (
    <div className={containerClasses}>
      <div className={contentClasses}>
        <svg
          className={classes.logo}
          viewBox="-21 270 388 529"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className={classes.part1}
            d="M263.286,773.994c-8.678,0.66 -22.53,1.108 -39.136,-2.128c-34.642,-6.75 -46.976,-14.491 -62.397,-22.839c-4.333,-2.346 -10.046,-6.81 -12.365,-8.162c-9.794,-5.71 -34.728,-30.733 -39.731,-37.527c-3.993,-5.421 -8.584,-10.451 -9.42,-11.647c-5.747,-8.224 -6.516,-7.554 -9.477,-11.611c-8.15,-11.166 -8.527,-10.827 -9.199,-11.819c-4.987,-7.364 -24.607,-30.212 -33.496,-36.357c-22.05,-15.245 -44.963,-8.245 -44.963,-8.245c0,0 0.408,-5.482 0.655,-7.048c2.954,-18.773 16.503,-33.938 21.64,-37.427c4.463,-3.031 8.41,-7.449 18.838,-11.505c12.784,-4.972 19.087,-4.574 28.001,-4.116c18.992,0.976 31.63,11.472 31.63,11.472c0,0 -3.661,8.616 -4.938,13.85c-5.323,21.825 6.916,53.242 6.916,53.242c0,0 -0.031,-7.321 -0.749,-25.387c-1.151,-28.992 9.077,-40.701 9.795,-41.788c8.733,-13.224 13.222,-16.835 30.111,-28.086c6.182,-4.118 23.762,-12.117 25.262,-12.72c8.278,-3.328 27.55,-9.955 38.085,-14.785c11.397,-5.224 41.623,-16.228 46.146,-17.748c37.166,-12.486 68.136,-50.474 68.136,-50.474c0,0 2.609,32.112 1.147,43.841c-1.946,15.614 -2.443,17.525 -2.83,19.013c-4.288,16.497 -6.068,23.569 -9.763,30.413c-6.374,11.809 -10.707,18.915 -18.197,26.367c-13.206,13.139 -26.662,20.249 -29.243,22.03c-12.489,8.613 -21.899,11.237 -36.346,24.73c-14.539,13.58 -16.438,25.721 -18.171,31.677c-10.664,36.641 5.618,74.526 8.71,79.443c0.661,1.051 4.449,8.728 12.21,18.141c7.067,8.571 21.802,22.289 33.136,27.2Z"
          />
          <path
            className={classes.part2}
            d="M127.073,521.7c-14.223,15.339 -21.227,28.995 -21.227,28.995c0,0 8.125,-39.008 17.407,-53.546c5.479,-8.58 14.308,-28.785 48.892,-56.845c1.22,-0.989 12.433,-10.087 15.423,-12.145c6.426,-4.42 6.142,-4.742 12.721,-8.838c5.53,-3.444 5.044,-4.07 10.657,-7.449c3.134,-1.886 42.984,-27.916 48.28,-31.661c20.67,-14.616 24.64,-18.254 26.605,-20.055c1.18,-1.082 1.495,-0.683 15.185,-13.033c11.812,-10.656 21.532,-23.431 23.207,-25.632c14.219,-18.687 17.26,-27.206 17.26,-27.206c0,0 1.073,25.895 -7.707,53.272c-2.229,6.951 -10.121,24.298 -14.17,30.16c-4.345,6.289 -4.138,7.681 -11.387,16.524c-4.21,5.135 -3.937,5.311 -8.525,10.1c-2.16,2.254 -2.692,3.431 -10.317,10.752c-20.218,19.411 -35.246,29.986 -90.282,59.914c-8.873,4.825 -29.85,15.001 -55.987,32.769c-1.029,0.7 -12.258,9.852 -16.034,13.925Z"
          />
        </svg>
        <div className={classes.mainTitle}>Verdin Labs</div>
        <div className={classes.subtitle}>Healthcare Platform</div>
        <div className={classes.spinner}></div>
      </div>
    </div>
  );
};

PageLoader.propTypes = {
  fullHeight: PropTypes.bool,
  isHiding: PropTypes.bool,
};

export default PageLoader;
